import { trpc, trpcRetry } from "@carrot/bff-client"
import { APPOINTMENTS_QUERY_KEY, APPOINTMENTS_SHARED_STALE_MS } from "@internal/constants/telemedicine/appointments"
import { Skeleton } from "@internal/ui"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import type { BookedAppointment, ScreeningProgram } from "../hooks"
import { useBookingOffering } from "../hooks"
import { useBookingTracking } from "../tracking"
import { appointmentTypeCopy } from "../preferencesCopy"
import {
  Box,
  Button,
  Divider,
  IconWithBackground,
  Tile,
  Typography,
  createLucideIcon,
  dayjs
} from "@carrotfertility/carotene-core"
import React from "react"
import { Heart, Check } from "lucide-react"

const CheckIcon = createLucideIcon(Check, "CheckIcon")
const HeartIcon = createLucideIcon(Heart, "HeartIcon")

type BookedPageProps = {
  program: ScreeningProgram
  // The appointment returned by the booking call — the source of the assigned provider and the
  // visit window. Null only if the screen is reached without a recorded booking (defensive).
  appointment: BookedAppointment | null
  // The offering the member booked; keys the offering lookup for the "Visit" line.
  offeringId?: string
}

// The booking confirmation ("You're booked") screen. Reached once the booking call succeeds; it
// summarizes the appointment and sends the member home. The confirmation email + calendar invite
// are server-side, and the join link is fetched just-in-time nearer the visit — neither is shown
// here (see MEM-719).
export function BookedPage({ program, appointment, offeringId }: BookedPageProps): JSX.Element {
  const tracking = useBookingTracking()
  // Offering is already cached from the preferences screen; this resolves the booked offering
  // to its display title + duration for the "Visit" line.
  const offeringQuery = useBookingOffering(program)
  const bookedOffering = offeringQuery.data?.offerings.find((offering) => offering.id === offeringId)

  // Reuse the member appointment cache; booking invalidates it on success. Match the exact
  // booking rather than the first upcoming visit or an assumption about its appointment type.
  const appointmentsQuery = useQuery({
    queryKey: APPOINTMENTS_QUERY_KEY,
    queryFn: () => trpc.telemedicine.getAppointments.query(),
    enabled: appointment !== null,
    staleTime: APPOINTMENTS_SHARED_STALE_MS,
    refetchOnWindowFocus: true,
    retry: trpcRetry(1)
  })
  const listedAppointment = appointmentsQuery.data?.upcoming.find((visit) => visit.id === appointment?.appointmentId)
  // Cached data takes precedence over fetch status so a background refresh (including a
  // failed one) never replaces valid actions with a skeleton or an error.
  let preparationState: "loading" | "forms-required" | "ready" | "error"
  if (!appointment) preparationState = "ready"
  else if (listedAppointment) preparationState = listedAppointment.formsComplete ? "ready" : "forms-required"
  else if (appointmentsQuery.isFetching) preparationState = "loading"
  else preparationState = "error"

  const detailRows = [
    appointment
      ? {
          label: "With",
          value: appointment.provider.credential
            ? `${appointment.provider.name}, ${appointment.provider.credential}`
            : appointment.provider.name
        }
      : null,
    appointment ? { label: "Date & time", value: dayjs(appointment.startTime).format("ddd, MMM D · h:mm A") } : null
  ].filter((row): row is { label: string; value: string } => row !== null)

  const linkToForms = appointment ? `/telemedicine/booking/${appointment.appointmentId}/forms` : "/"

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(theme.tokens.spacing.xl),
        maxInlineSize: 640,
        marginInline: "auto",
        textAlign: "center"
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <IconWithBackground color="success" icon={CheckIcon} />
      </Box>

      <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xs) })}>
        <Typography variant="h2" component="h1">
          You&apos;re booked
        </Typography>
      </Box>
      <Tile
        variant="primary"
        sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
      >
        <Box sx={(theme) => ({ display: "flex", alignItems: "center", gap: theme.spacing(theme.tokens.spacing.sm) })}>
          <IconWithBackground color="primary" icon={HeartIcon} />
          {offeringQuery.isLoading ? (
            <Skeleton width="70%" height={28} />
          ) : (
            <Typography variant="h3">
              {bookedOffering
                ? `${bookedOffering.durationMinutes} minute ${
                    appointmentTypeCopy(bookedOffering.appointmentType).title
                  }`
                : "Your visit"}
            </Typography>
          )}
        </Box>
        <Divider />
        {detailRows.map((row) => (
          <Box
            key={row.label}
            sx={(theme) => ({
              display: "flex",
              inlineSize: "100%",
              gap: theme.spacing(theme.tokens.spacing.md)
            })}
          >
            <Typography
              sx={(theme) => ({ textAlign: "start", color: theme.palette.text.tertiary, inlineSize: "40%" })}
              component="dt"
            >
              {row.label}
            </Typography>
            <Typography component="dd" sx={{ margin: 0, textAlign: "start" }}>
              {row.value}
            </Typography>
          </Box>
        ))}
      </Tile>
      {preparationState === "loading" && (
        <Box
          role="status"
          aria-label="Checking for appointment forms"
          aria-busy="true"
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            inlineSize: "100%",
            gap: theme.spacing(theme.tokens.spacing.md)
          })}
        >
          <Skeleton width="75%" height={28} />
          <Skeleton width="90%" height={20} />
          <Skeleton width="65%" height={20} />
          <Skeleton width={180} height={44} />
        </Box>
      )}
      {preparationState === "forms-required" && (
        <>
          <Typography variant="h3">Take a moment to prepare for your appointment.</Typography>
          <Typography variant="body1">
            We&apos;ll need your identification and a few details about your medical history prior to your appointment
          </Typography>
          <Box sx={(theme) => ({ display: "flex", gap: theme.spacing(theme.tokens.spacing.md) })}>
            <Button
              color="secondary"
              component={Link}
              to={linkToForms}
              onClick={() => {
                if (appointment) tracking.onEvent({ type: "prepare", appointmentId: appointment.appointmentId })
              }}
            >
              Complete forms
            </Button>
            <Button component={Link} variant="outlined" color="secondary" to="/">
              Not now
            </Button>
          </Box>
        </>
      )}
      {(preparationState === "ready" || preparationState === "error") && (
        <>
          {preparationState === "error" && (
            <>
              <Typography role="status" variant="body1">
                Your appointment is booked, but we couldn’t check your preparation requirements.
              </Typography>
              <Button color="secondary" onClick={() => void appointmentsQuery.refetch()}>
                Try again
              </Button>
            </>
          )}
          <Button component={Link} color="secondary" to="/">
            Back to home
          </Button>
        </>
      )}
    </Box>
  )
}
